// src/features/records/components/CalendarView/ListView/RecordListItem.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toDate } from '../utils/dateHelpers';
import { sanitizeText } from '../../../../../utils/sanitize';
import { Avatar, AvatarSkeleton } from '../../../../../components/avatars';
import { resolveTimeZone } from '../../../../../utils/dateUtils';
import styles from './ListView.module.css';

const RecordListItem = ({ 
  record, 
  isHovered, 
  onMouseEnter, 
  onMouseLeave, 
  onClick,
  userProfiles = {},
  profilesLoading = false
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const { content = {}, attributes = {} } = record;
  const { title = t('records.views.calendar.item.untitled'), body } = content;
  const { date = {} } = attributes;
  const { start, end, isAllDay, timezone } = date;
  const safeTimeZone = resolveTimeZone(timezone);

  const formatTimeValue = (value) => {
    const parsed = toDate(value);
    if (!parsed || Number.isNaN(parsed.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
    }).format(parsed);
  };
  
  // Prefer record-scoped accent so avatar fallback matches calendar pills
  const categoryColor = record.categoryColor || record.calendarColor || 'var(--accent-primary)';

  const ownerId = record?.access?.owner ?? record?.ownerId ?? null;
  const recordOwnerProfile =
    (ownerId && userProfiles[ownerId]) ||
    record?.ownerProfile ||
    null;

  const ownerName =
    recordOwnerProfile?.displayName ||
    recordOwnerProfile?.username ||
    record?.ownerName ||
    t('records.views.calendar.item.user_fallback_name');

  const ownerAvatarSrc =
    recordOwnerProfile?.profilePictureUrl ||
    recordOwnerProfile?.profilePicture ||
    recordOwnerProfile?.photoURL ||
    null;

  const shouldShowAvatarSkeleton = Boolean(profilesLoading && ownerId && !recordOwnerProfile);
  const avatarColorSeed = ownerId || record.id || ownerName;
  const avatarFallbackColor = categoryColor;
  
  return (
    <div
      className={`${styles.recordListItem} ${isHovered ? styles.recordListItemHover : ''}`}
      style={{ borderLeftColor: categoryColor }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.avatarSlot}>
        {shouldShowAvatarSkeleton ? (
          <AvatarSkeleton size="sm" className={styles.ownerAvatarSkeleton} />
        ) : (
          <Avatar
            size="sm"
            className={styles.ownerAvatar}
            name={ownerName}
            colorSeed={avatarColorSeed}
            fallbackColor={avatarFallbackColor}
            src={ownerAvatarSrc || undefined}
            alt={
              ownerName
                ? t('records.views.calendar.item.user_avatar_alt', { name: ownerName })
                : t('records.views.calendar.item.user_avatar_fallback_alt')
            }
          />
        )}
      </div>

      <div className={styles.contentArea}>
        <div className={styles.recordTitle}>
          {title}
        </div>
        {body && (
          <div className={styles.recordBody}>
            {sanitizeText(body)}
          </div>
        )}
      </div>
      
      <div className={styles.timeInfo}>
        {isAllDay ? (
          <span className={styles.allDayBadge}>{t('records.time.all_day')}</span>
        ) : (
          <>
            <div className={`${styles.timeText} ${styles.startTime}`}>
              {start ? formatTimeValue(start) : ''}
            </div>
            {end && (
              <div className={`${styles.timeText} ${styles.endTime}`}>
                {formatTimeValue(end)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RecordListItem;
